/**
 * The prejoin "Connection" check: a real round-trip-time measurement to the control service's
 * operational /healthz endpoint (same-origin; dev serves it through the Vite proxy). The fetch is
 * keepalive + no-store so it never queues behind page teardown and never serves a cached answer.
 * Every failure degrades to null ("unreachable") instead of throwing.
 */

/** How long the probe waits before giving up; a slower control service reads as unreachable. */
const RTT_TIMEOUT_MS = 5000;

/**
 * Round-trip time to the control service in milliseconds, or null when the request failed,
 * timed out or reported a non-OK status.
 */
export async function measureControlRtt(
  fetchFn: typeof fetch = fetch,
  path: string = '/healthz',
): Promise<number | null> {
  const started = performance.now();
  try {
    const response = await fetchFn(path, {
      method: 'GET',
      cache: 'no-store',
      keepalive: true,
      signal: AbortSignal.timeout(RTT_TIMEOUT_MS),
    });
    if (!response.ok) {
      return null;
    }
  } catch {
    return null;
  }
  return Math.round(performance.now() - started);
}

/** Wording tier of the measured connection quality; drives the check-row value and the signal bars. */
export type ConnectionQuality = 'excellent' | 'good' | 'weak' | 'down';

/** RTT at or below this reads as "excellent" (same-metro / loopback latency). */
const EXCELLENT_MS = 80;
/** RTT at or below this still reads as "good" (cross-region within a continent). */
const GOOD_MS = 250;

/**
 * Maps the measured RTT to a quality tier. Thresholds are deliberately generous: the probe
 * measures an HTTPS handshake round trip, not the media path.
 */
export function connectionQuality(rttMs: number | null): ConnectionQuality {
  if (rttMs === null) {
    return 'down';
  }
  if (rttMs <= EXCELLENT_MS) {
    return 'excellent';
  }
  if (rttMs <= GOOD_MS) {
    return 'good';
  }
  return 'weak';
}

/** Signal-bars level (1..3) for the mockup's quality chip; an unreachable control still shows one bar. */
export function qualitySignalLevel(quality: ConnectionQuality): 1 | 2 | 3 {
  switch (quality) {
    case 'excellent':
      return 3;
    case 'good':
      return 2;
    case 'weak':
    case 'down':
      return 1;
  }
}
